import React from 'react';
import { SubmissionErrors } from 'final-form';
import { Field, Form } from 'react-final-form';
import { useTranslation } from '../../utils/useTranslation';
import { composeValidators } from '../../utils/validators/compose';
import { makeMaxLengthValidator } from '../../utils/validators/makeMaxLengthValidator';
import { makeRequiredValidator } from '../../utils/validators/makeRequiredValidator';
import { Button } from '../input/Button';
import { FormControl } from '../input/FormControl';
import { FormControlLabel, LabelPlacement } from '../input/FormControlLabel';
import { Input } from '../input/Input';
import { Switch } from '../input/Switch';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { CollectionPersistenceUI } from './types';

const validateDisplayName = composeValidators(
    makeRequiredValidator<string>(),
    makeMaxLengthValidator(25, 'Название коллекции не должно превышать 25 символов'),
);

interface CollectionPersistenceFormProps {
    initialValues?: Partial<CollectionPersistenceUI>;
    onSubmit: (formValues: CollectionPersistenceUI) => Promise<SubmissionErrors>;
}

export const CollectionPersistenceForm = (props: CollectionPersistenceFormProps) => {
    const { initialValues = {}, onSubmit } = props;
    const t = useTranslation();

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ submitting, pristine, submitError, handleSubmit }) => (
                <Flex
                    p={2}
                    as="form"
                    width="100%"
                    direction="column"
                    height={{ xs: '100%', sm: 'auto' }}
                    borderRadius={{ sm: 2 }}
                    backgroundColor="var(--background-primary)"
                    onSubmit={handleSubmit}
                >
                    <Field
                        name="displayName"
                        validate={validateDisplayName}
                        render={({ input, meta }) => (
                            <FormControl
                                mb={2}
                                required
                                label="Название коллекции"
                                error={meta.touched && Boolean(meta.error)}
                                message={meta.touched ? meta.error : undefined}
                            >
                                <Input
                                    {...input}
                                    type="text"
                                    autoComplete="off"
                                    placeholder="Название коллекции"
                                />
                            </FormControl>
                        )}
                    />
                    <Field
                        type="checkbox"
                        name="isPublic"
                        render={({ input }) => (
                            <FormControlLabel
                                label={
                                    <>
                                        <Typography>{t('Публичная коллекция')}</Typography>
                                        <Typography variant="caption" color="secondary">
                                            {t(
                                                'Чтобы коллекцией можно было поделиться, ее нужно сделать публичной. Мы быстро проверим заголовок, и функция «Поделиться» станет доступна.',
                                            )}
                                        </Typography>
                                    </>
                                }
                                control={<Switch {...input} />}
                                labelPlacement={LabelPlacement.LEFT}
                                labelWidth={{ xs: '80%', sm: '90%' }}
                            />
                        )}
                    />
                    <Box mt="auto" width="100%">
                        {submitError && (
                            <Typography variant="caption" color="error" mb={2}>
                                {submitError}
                            </Typography>
                        )}
                        <Button width="100%" type="submit" disabled={pristine || submitting}>
                            Обновить
                        </Button>
                    </Box>
                </Flex>
            )}
        />
    );
};
